/**
 * Phase 6e.3 — `applyFxSyncPlan(engagement, plan)` store action.
 *
 * Pure function. Returns a NEW `EngagementHistoryEntry` with the
 * plan applied; the caller (Magic Button) is responsible for
 * persisting via `historyStorage.saveEngagement(...)` and
 * propagating to local React state through the existing
 * `onEngagementUpdated` callback chain.
 *
 * Apply rules (from the Phase 6e.3 spec + user constraint):
 *
 *   1. Per FX contract, the matcher emits a `ContractFxUpdate`.
 *      We OVERWRITE the contract's `commencementSpotRate`,
 *      `historicalRates[]`, and `paymentDateRates[]` only when at
 *      LEAST ONE rate matched on that contract (clean or T-1).
 *      Contracts where every lookup failed (e.g. unsupported
 *      currency, all dates out of range) are NOT touched — the
 *      auditor's prior manual entries survive.
 *
 *   2. Engagement-level `closingFxRates[CCY]` is overwritten per
 *      currency when its `RateLookupResult` is non-null. Existing
 *      values for unmatched currencies are preserved.
 *
 *   3. `engagement.lastFxSync` is set to `plan.audit` so the audit
 *      accordion can render across re-renders without re-fetching.
 *
 * The matcher's `historicalRates` / `paymentDateRates` items each
 * carry the canonical `ContractInput`-shape `{ date, rate }` plus
 * the matcher's diagnostic fields (requestedDate, fellBackDays,
 * intended). We extract only the first two so the contract array
 * matches the existing schema downstream consumers expect.
 */

import type { ContractInput, EngagementHistoryEntry } from "@/types";

import type { ContractFxUpdate, FxSyncPlan } from "./types";

export function applyFxSyncPlan(
  engagement: EngagementHistoryEntry,
  plan: FxSyncPlan,
): EngagementHistoryEntry {
  // Build a lookup of which contract gets which update, keyed by
  // contractId (engagement.contracts is a sparse array of nullable
  // slots; matching by index would be fragile across edits).
  const updatesByContractId = new Map<string, ContractFxUpdate>();
  for (const u of plan.contractUpdates) {
    updatesByContractId.set(u.contractId, u);
  }

  const nextContracts: Array<ContractInput | null> = engagement.contracts.map(
    (c) => {
      if (c === null) return null;
      const u = updatesByContractId.get(c.contractId);
      if (!u) return c;
      if (!contractHadAnyMatch(u)) return c;
      return applyOneContractUpdate(c, u);
    },
  );

  // closingFxRates: shallow merge per currency. Pre-existing entries
  // for currencies that didn't match are preserved (no silent blanks).
  const nextClosing: Record<string, string> = {
    ...(engagement.closingFxRates ?? {}),
  };
  for (const e of plan.engagementClosingRates) {
    if (e.rate) {
      nextClosing[e.currency] = String(e.rate.rate);
    }
  }

  return {
    ...engagement,
    contracts: nextContracts,
    closingFxRates: nextClosing,
    lastFxSync: plan.audit,
    updatedAt: new Date().toISOString(),
  };
}

/**
 * "Had at least one match" = commencementSpotRate matched OR at
 * least one historical OR at least one payment-date rate matched.
 * Errors-only contracts (the entire FX dataset failed) leave the
 * existing manual entries untouched.
 */
function contractHadAnyMatch(u: ContractFxUpdate): boolean {
  return (
    u.commencementSpotRate !== null ||
    u.historicalRates.length > 0 ||
    u.paymentDateRates.length > 0
  );
}

function applyOneContractUpdate(
  contract: ContractInput,
  u: ContractFxUpdate,
): ContractInput {
  const next: ContractInput = { ...contract };
  if (u.commencementSpotRate !== null) {
    next.commencementSpotRate = u.commencementSpotRate.rate;
  }
  // Overwrite arrays whenever the contract had ANY match — even if
  // the array itself is empty in the plan (means every historical
  // date failed). The plan's `errors[]` carries the per-date failure
  // reason for the audit accordion; the contract simply ends up
  // with an empty array.
  next.historicalRates = u.historicalRates.map((r) => ({
    date: r.intended,
    rate: r.rate,
  }));
  next.paymentDateRates = u.paymentDateRates.map((r) => ({
    date: r.intended,
    rate: r.rate,
  }));
  return next;
}
